/* eslint-disable import/no-cycle */
import { Mutation } from '@apollo/client/react/components';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { withSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { ACCEPT_TOS, GET_LOGIN_TOKEN } from '../../helpers/graphql/token';
import graphQLClient from '../../helpers/graphQLClient';
import LoginDialog from '../Login/LoginDialog';
import PasswordPicker from '../Onboarding/PasswordPicker';
import JournalField from '../Onboarding/JournalField';

const EasyLoginButton = props => {
  const [usernameOrEmail, setUsernameOrEmail] = useState(undefined);
  const [password, setPassword] = useState(undefined);
  const [loginData, setLoginData] = useState(undefined);
  const [showPassword, setShowPassword] = useState(false);

  const newNotification = notification => {
    if (notification !== undefined) {
      let variant = 'success';
      if (notification.success === false) {
        variant = 'error';
      }
      const { enqueueSnackbar } = props;
      enqueueSnackbar(notification.message, { variant });
    }
  };

  const handleLogin = () => {
    graphQLClient(GET_LOGIN_TOKEN, {
      usernameOrEmail,
      password,
    })
      .then(({ login }) => {
        if (login.success && login.needsEmailConfirmation) {
          newNotification({
            success: false,
            message: `You need to confirm your email before you can log in`,
          });
        } else if (login.success && !login.hasAcceptedTos) {
          setLoginData(login);
        } else if (login.success && login.hasAcceptedTos) {
          setLoginData(login);
          // Force hard reload on login to update apollo context with new access token
          window.open('/dashboard', '_self', undefined, true);
        } else {
          newNotification({
            success: false,
            message: `The login credentials you provided are not valid. Please log in again.`,
          });
          props.setShowReset(true);
        }
      })
      .catch(() => setLoginData({}));
  };

  const handleUsernameEmailInput = event => {
    setUsernameOrEmail(event.target.value.toLowerCase());
  };

  const handleEasyLogin = () => {
    if (usernameOrEmail && password) handleLogin();
    else
      newNotification({
        success: false,
        message: 'Enter your username and password first',
      });
  };

  const handleEasyKeyPress = event => {
    if (event.key === 'Enter') handleEasyLogin();
  };

  if (loginData) {
    // If tos are not accepted, display tos dialogue
    if (loginData.success && !loginData.hasAcceptedTos) {
      return (
        <Mutation
          mutation={ACCEPT_TOS}
          variables={{
            token: loginData.signUpToken,
          }}
        >
          {(acceptTos, { data: mutatedata }) => {
            // If successful
            if (mutatedata && mutatedata.acceptTos) {
              if (mutatedata.acceptTos && mutatedata.acceptTos.success) {
                window.open('/dashboard', '_self', undefined, true);
                return <CircularProgress />;
              }
              newNotification({
                success: false,
                message: `The login credentials you provided are not valid. Please log in again.`,
              });
            }
            return <LoginDialog acceptTos={acceptTos} />;
          }}
        </Mutation>
      );
    }
    return <CircularProgress />;
  }
  // Expedition-journal skin (auth dialogs); the default keeps the legacy
  // confirmEmail page unchanged.
  if (props.journal)
    return (
      <>
        <JournalField
          label="Email or username"
          id="username"
          type="text"
          autoFocus
          value={usernameOrEmail || ''}
          onChange={handleUsernameEmailInput}
          onKeyPress={handleEasyKeyPress}
          className="tw-mb-4"
        />
        <JournalField
          label="Password"
          id="login-password"
          type={showPassword ? 'text' : 'password'}
          value={password || ''}
          onChange={e => setPassword(e.target.value)}
          onKeyPress={handleEasyKeyPress}
          autoComplete="current-password"
          rightSlot={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="tw-flex tw-cursor-pointer tw-items-center tw-border-0 tw-bg-transparent tw-p-1 tw-text-[#8A958F] hover:tw-text-primary-700 dark:hover:tw-text-primary-300"
            >
              {showPassword ? (
                <svg
                  className="tw-h-5 tw-w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="tw-h-5 tw-w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          }
        />
        <button
          type="button"
          onClick={handleEasyLogin}
          className="tw-mt-5 tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-primary-600 tw-px-6 tw-py-3.5 tw-text-base tw-font-semibold tw-text-white tw-shadow-[0_10px_30px_-10px_rgba(0,137,123,0.7)] tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-bg-primary-700"
        >
          Log in
        </button>
      </>
    );

  return (
    <>
      <TextField
        variant="standard"
        autoFocus
        onChange={handleUsernameEmailInput}
        margin="dense"
        id="username"
        label="Email or username"
        value={usernameOrEmail}
        fullWidth
        onKeyPress={handleEasyKeyPress}
      />
      <PasswordPicker
        label="Password"
        setPassword={setPassword}
        onKeyPress={handleEasyKeyPress}
      />
      <div className="pt-4 pb-2">
        <Button
          fullWidth={props.fullWidth}
          fullsize
          onClick={handleEasyLogin}
          color="primary"
          variant="contained"
          size="large"
        >
          Login
        </Button>
      </div>
    </>
  );
};

EasyLoginButton.propTypes = {
  enqueueSnackbar: PropTypes.func.isRequired,
};

export default withSnackbar(EasyLoginButton);
